import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { SCREENSHOTS_MAX, type FeedbackScreenshot } from '@higgins/utils';
import { shrinkImage } from '../lib/shrink-image';
import { Button } from './ui/button';

interface ScreenshotPickerProps {
  value: FeedbackScreenshot[];
  onChange: (update: (current: FeedbackScreenshot[]) => FeedbackScreenshot[]) => void;
}

/**
 * The report panel's optional pictures, up to SCREENSHOTS_MAX: choose them (on the iPad:
 * Photos, the camera or Files) or paste one while the panel is open. Each is shrunk on
 * the device straight away.
 */
export function ScreenshotPicker({ value, onChange }: ScreenshotPickerProps) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const room = SCREENSHOTS_MAX - value.length;

  async function take(files: File[]) {
    setBusy(true);
    setProblem(null);
    try {
      const accepted = files.slice(0, Math.max(0, SCREENSHOTS_MAX - value.length));
      if (accepted.length < files.length) setProblem(`At most ${SCREENSHOTS_MAX} pictures; the rest were left out.`);
      for (const file of accepted) {
        const shot = await shrinkImage(file, file.name || 'pasted.png');
        onChange((current) => (current.length < SCREENSHOTS_MAX ? [...current, shot] : current));
      }
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Paste anywhere while the panel is open (a desk screenshot with Win+Shift+S, then Ctrl+V).
  const takeRef = useRef(take);
  takeRef.current = take;
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const files = [...(e.clipboardData?.files ?? [])].filter((f) => f.type.startsWith('image/'));
      if (files.length === 0) return;
      e.preventDefault();
      void takeRef.current(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, []);

  return (
    <div className="space-y-2">
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          if (files.length > 0) void take(files);
        }}
      />
      {value.length > 0 && (
        <ul className="flex flex-wrap gap-2">
          {value.map((shot, i) => (
            <li key={`${i}-${shot.fileName}`} className="relative">
              <img src={`data:image/jpeg;base64,${shot.base64}`} alt={`Picture ${i + 1}`} className="h-20 w-20 rounded border border-slate-200 object-cover" />
              <button
                type="button"
                onClick={() => onChange((current) => current.filter((_, j) => j !== i))}
                aria-label={`Remove picture ${i + 1}`}
                className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {room > 0 && (
        <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()} disabled={busy} className="min-h-11">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImagePlus className="mr-2 h-4 w-4" />}
          {value.length === 0 ? 'Add a screenshot' : 'Add another'}
        </Button>
      )}
      <p className="text-xs text-slate-500">
        {value.length === 0 ? `Optional, up to ${SCREENSHOTS_MAX}. You can also paste one.` : `${value.length} of ${SCREENSHOTS_MAX} pictures.`}
      </p>
      {problem && (
        <p role="alert" className="text-sm text-red-700">
          {problem}
        </p>
      )}
    </div>
  );
}
